import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import {
  Pagination,
  PaginationCompact,
  PaginationItem
} from '@one-thd/sui-atomic-components';

/**
 * Builds/updates query params in a path string.
 * Updates the Nao parameter for pagination and removes it for the first page.
 * Removes CI-5 parameter to prevent unnecessary bot crawling on pagination links beyond the first page.
 */
const updateParams = (path, pageSize, pageNum = 1) => {
  const [basePath, queryString = ''] = path.split('?');

  /**
   * Treat page numbers <= 1 as first page preventing negative offsets and
   * ensuring the Nao parameter is only included for valid pagination links
   */
  const isFirstPage = pageNum <= 1;
  const offset = pageSize * (pageNum - 1);

  // Use URLSearchParams for proper query string manipulation
  const params = new URLSearchParams(queryString);

  // Update or remove Nao pagination parameter
  if (isFirstPage) {
    params.delete('Nao');
  } else {
    // Remove CI-5 parameter to prevent bot crawling on pagination links beyond the first page
    params.delete('CI-5');
    params.set('Nao', offset);
  }

  // URLSearchParams adds '=' for valueless params
  const updatedQueryString = params.toString().replace(/=(?=&|$)/g, '');
  return `${basePath}${updatedQueryString ? `?${updatedQueryString}` : ''}`;
};

/**
 * Determines whether a pagination item should have an href attribute.
 * Excludes hrefs for: last page buttons, selected (current) page, and non-consecutive last page numbers.
 */
const shouldIncludeHref = (item, page, count) => {
  const currentPage = page ?? 1;
  const isLastPage = item.page === count;
  const isLastPageConsecutive = isLastPage && currentPage === count - 1;

  if (item.type === 'last') {
    // Mobile >> button: never linked so the last page is not emphasized in importance
    return false;
  }
  if (item.type === 'page' && item.selected) {
    // Current page number should not be linked
    return false;
  }
  if (item.type === 'page' && isLastPage && !isLastPageConsecutive) {
    // Last page number should not be linked unless it is consecutive to the current page
    return false;
  }

  // previous, next, first nav buttons: always linked
  return item?.page != null;
};

const ProductResultsPageLinks = ({
  channel = 'desktop',
  paginationProps = {},
  path = '',
  pageSize = 24,
}) => {
  const { channel: contextChannel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile' || contextChannel === 'mobile';

  const {
    page,
    count = 1
  } = paginationProps;

  const getPaginationItem = (item) => {
    const includeHref = !!path.trim() && shouldIncludeHref(item, page, count);

    return (
      <PaginationItem
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...item}
        href={includeHref ? updateParams(path, pageSize, item.page) : undefined}
      />
    );
  };

  const componentProps = {
    ...paginationProps,
    renderItem: getPaginationItem
  };

  return (
    <div data-component="ProductResultsPageLinks">
      {isMobile
        // eslint-disable-next-line react/jsx-props-no-spreading
        ? <PaginationCompact {...componentProps} showFirstButton showLastButton />
        // eslint-disable-next-line react/jsx-props-no-spreading
        : <Pagination {...componentProps} />}
    </div>
  );
};

ProductResultsPageLinks.displayName = 'ProductResultsPageLinks';

ProductResultsPageLinks.propTypes = {
  channel: PropTypes.string,
  paginationProps: PropTypes.shape({}),
  path: PropTypes.string,
  pageSize: PropTypes.number
};

export { ProductResultsPageLinks };